import { Component, inject } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import jwt_decode from 'jwt-decode';
import { PersonalProfileState } from '../../../libs/state/personal-profile.state';
import { ProjectsState } from '../../../libs/state/project.state';
import { hasFinxoneAdminRole } from '../../../libs/utils/finxone-admin-role.utils';

@Component({
  standalone: false,
  selector: 'finxone-workflow-frontend-side-navbar',
  templateUrl: './side-navbar.component.html',
  styleUrls: ['./side-navbar.component.scss'],
})
export class SideNavbarComponent {
  activeRoute = '';
  sideLinksData: {
    route: string;
    label: string;
    icon: string;
    active_icon: string;
    adminOnly?: boolean;
    /** Hidden for code projects: these edit the configured experience, which they have none of. */
    configuredOnly?: boolean;
  }[] = [
    {
      route: '/back-office/dashboard',
      label: 'Back to Dashboard',
      icon: '/assets/images/sidebar-icons/grid.svg',
      active_icon: '/assets/images/sidebar-icons/grid-active.svg',
    },
    {
      route: '/ui-builder/client-dashboard',
      label: 'Client Dashboard',
      icon: '/assets/images/sidebar-icons/client-dashboard.svg',
      active_icon: '/assets/images/sidebar-icons/client-dashboard-active.svg',
      configuredOnly: true,
    },
    {
      route: '/ui-builder/application-editor',
      label: 'App Grid',
      icon: '/assets/images/sidebar-icons/app-grid.svg',
      active_icon: '/assets/images/sidebar-icons/app-grid-active.svg',
      configuredOnly: true,
    },
    {
      route: '/ui-builder/project-level-settings',
      label: 'Settings',
      icon: '/assets/images/sidebar-icons/settings.svg',
      active_icon: '/assets/images/sidebar-icons/settings-active.svg',
    },
    {
      route: '/ui-builder/alert-management',
      label: 'Alert Management',
      icon: '/assets/images/sidebar-icons/wifi_tethering_error_1.svg',
      active_icon: '/assets/images/sidebar-icons/wifi_tethering_error_active.svg',
      configuredOnly: true,
    },
    {
      route: '/ui-builder/notification-workflow-settings',
      label: 'Notification',
      icon: 'assets/images/sidebar-icons/notification-inactive.svg',
      active_icon: 'assets/images/sidebar-icons/notification.svg',
    },
    {
      route: '/ui-builder/authentication-security',
      label: 'Authentication & Security',
      icon: 'assets/images/sidebar-icons/encrypted.svg',
      active_icon: 'assets/images/sidebar-icons/encrypted-active.svg',
    },
    {
      route: '/ui-builder/global-customisation',
      label: 'Global Customisation',
      icon: '/assets/images/sidebar-icons/global.svg',
      active_icon: '/assets/images/sidebar-icons/global-active.svg',
    },
    {
      route: '/ui-builder/navigationbar',
      label: 'Navigation Bar',
      icon: '/assets/images/sidebar-icons/navigationbar.svg',
      active_icon: '/assets/images/sidebar-icons/navigationbar-active.svg',
      configuredOnly: true,
    },
    {
      route: '/ui-builder/custom-widgets',
      label: 'Custom Widgets',
      icon: '/assets/images/sidebar-icons/widget.svg',
      active_icon: '/assets/images/sidebar-icons/widget-active.svg',
      adminOnly: true,
      configuredOnly: true,
    },
  ];
  hamburgerPath = 'assets/images/hamburger-icon-light.svg';
  timesPath = 'assets/images/times.svg';
  isSidebarOpen = false;
  isShowLabel = false;
  previousRoute = '';

  userInitials = '';
  userAdmin = false;
  private readonly _router = inject(Router);
  private readonly _store = inject(Store);

  /** A code project's experience lives in App Studio, not in workflow_config. */
  codeExperience = false;
  appStudioEnabled = false;

  public get visibleLinksData() {
    return this.sideLinksData.filter(
      (link) => (!link.adminOnly || this.userAdmin) && !(link.configuredOnly && this.codeExperience),
    );
  }

  constructor() {
    this.userAdmin = this.isUserAdmin();
    // Infrastructure lives in the project context but is finxone-admin only
    // (ADR-9); the API is the real boundary, this only hides the entry.
    if (hasFinxoneAdminRole()) {
      const settingsIndex = this.sideLinksData.findIndex(
        (link) => link.route === '/ui-builder/project-level-settings',
      );
      this.sideLinksData.splice(settingsIndex + 1, 0, {
        route: '/ui-builder/infrastructure',
        label: 'Infrastructure',
        icon: '/assets/images/sidebar-icons/database.svg',
        active_icon: '/assets/images/sidebar-icons/database-active.svg',
      });
    }
    // Functions is finxone-admin only. Icons are reused from the registered set; no new assets.
    if (!this.sideLinksData.some((link) => link.route === '/ui-builder/custom-functions')) {
      this.sideLinksData.push({
        route: '/ui-builder/custom-functions',
        label: 'Functions',
        icon: '/assets/images/sidebar-icons/workflow.svg',
        active_icon: '/assets/images/sidebar-icons/workflow-active.svg',
        adminOnly: true,
        configuredOnly: true,
      });
    }
    this._store.select(ProjectsState.getActiveProject).subscribe((project) => {
      this.codeExperience = project?.experienceKind === 'code';
      this.appStudioEnabled = this.codeExperience || project?.appStudioEnabled === true;
      this.syncExperienceLinks();
    });
    this._store.select(PersonalProfileState.getPersonalProfileDetail).subscribe((user) => {
      this.userInitials = `${user?.firstName?.charAt(0) ?? ''}${user?.lastName?.charAt(0) ?? ''}`;
    });
    this.activeRoute = this._router.url; // assign the current route as default active route when component is initialized
    this._router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        const currentRoute = event.url;
        if (currentRoute !== this.previousRoute) {
          this.activeRoute = currentRoute;
          this.isSidebarOpen = false;
          this.isShowLabel = false;
        }
        this.previousRoute = currentRoute;
      }
    });
  }

  /** Prepared legacy projects retain their configured editor alongside App Studio. */
  private syncExperienceLinks(): void {
    const index = this.sideLinksData.findIndex((link) => link.route === '/ui-builder/app-studio');
    const wanted = this.appStudioEnabled;
    if (wanted && index === -1) {
      const settingsIndex = this.sideLinksData.findIndex(
        (link) => link.route === '/ui-builder/project-level-settings',
      );
      this.sideLinksData.splice(Math.max(settingsIndex, 0), 0, {
        route: '/ui-builder/app-studio',
        label: 'App Studio',
        icon: '/assets/images/sidebar-icons/widget.svg',
        active_icon: '/assets/images/sidebar-icons/widget-active.svg',
      });
    }
    if (!wanted && index !== -1) {
      this.sideLinksData.splice(index, 1);
    }
    this.syncPhoneBuildsLink(wanted);
  }

  /** Phone builds signs a code project's mobile app; a configured project has none to sign. */
  private syncPhoneBuildsLink(wanted: boolean): void {
    const index = this.sideLinksData.findIndex((link) => link.route === '/ui-builder/phone-builds');
    if (wanted && index === -1) {
      const settingsIndex = this.sideLinksData.findIndex(
        (link) => link.route === '/ui-builder/project-level-settings',
      );
      this.sideLinksData.splice(settingsIndex + 1, 0, {
        route: '/ui-builder/phone-builds',
        label: 'Phone builds',
        icon: '/assets/images/sidebar-icons/phone.svg',
        active_icon: '/assets/images/sidebar-icons/phone-active.svg',
      });
    }
    if (!wanted && index !== -1) {
      this.sideLinksData.splice(index, 1);
    }
  }

  private isUserAdmin(): boolean {
    const token = sessionStorage.getItem('token');
    if (!token) {
      return false;
    }
    try {
      const jwt = jwt_decode(token) as { realm_access?: { roles?: string[] } };
      return jwt.realm_access?.roles?.includes('finxone-admin') ?? false;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  }

  navigateToRoute(path: string) {
    if (path !== '#') {
      this._router.navigateByUrl(path);
    }
  }

  toggleSideBar(type: string) {
    if (type === 'open') {
      this.isSidebarOpen = !this.isSidebarOpen;
      setTimeout(() => {
        this.isShowLabel = true;
      }, 330);
    } else {
      this.isShowLabel = false;
      this.isSidebarOpen = !this.isSidebarOpen;
    }
  }
}
